/**
 * <CallBriefView /> — the Deal Brief as ONE page.
 *
 * The champion forwards this to their economic buyer, who gives it about 30
 * seconds. So the page is built as a hierarchy, not a document: the headline is
 * the biggest thing on it, three numbers sit under it, the before→after diagram
 * carries the argument visually, and the supporting detail is packed into two
 * tight columns that reward a second glance without demanding one.
 *
 * Everything is sized to fit a single A4 sheet on export — the print block in
 * index.css tightens the same structure rather than reflowing it, so what the
 * rep sees on screen is what the PDF is. Export PDF hoists the article out so
 * only it prints (no blank pages). `showActions={false}` renders the public /r
 * view (no action bar).
 */
import { useRef, useState } from 'react';
import MoxoLogo from './MoxoLogo';
import BeforeAfterFlow from './BeforeAfterFlow';
import { buildPublicCallBriefSharePayload } from '../../lib/share-call-brief';
import { buildShareUrl } from '../../lib/share-report';
import type { CallBriefResult } from './call-brief-types';

const REGION_LABELS: Record<string, string> = {
  north_america: 'North America',
  uk: 'the UK',
  western_europe: 'Western Europe',
  anz: 'Australia / NZ',
  singapore: 'Singapore',
  apac_developed: 'developed APAC',
  mena: 'the Middle East',
  eastern_europe: 'Eastern Europe',
  latam: 'Latin America',
  india: 'India',
  apac_other: 'APAC',
  africa: 'Africa',
};
const regionLabel = (r: string): string => REGION_LABELS[r] ?? r;

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function CallBriefView({
  result,
  showActions = true,
}: {
  result: CallBriefResult;
  showActions?: boolean;
}) {
  const articleRef = useRef<HTMLElement>(null);
  const [shareCopied, setShareCopied] = useState(false);
  const brief = result.brief;
  if (!brief) return null;
  const roi = result.roi;
  const { company } = brief;

  // The honest, testifiable basis for the ROI number — powered by the actual
  // assumptions the engine used, so the tooltip (and the print caption) show
  // exactly where the figure comes from.
  const a = roi?.assumptions;
  const roiBasis =
    a && roi
      ? `The maths behind this: about ${a.runsPerYear.toLocaleString('en-US')} runs a year × ${a.peopleInvolved} of us × ${a.hoursSavedPerRun} hrs of manual work saved per run, held to a conservative 60% ≈ ${Math.round(
          roi.annualHoursFreed,
        ).toLocaleString('en-US')} hours a year — valued at a fully-loaded ~$${a.loadedHourlyCost}/hr for ${regionLabel(a.region)} = $${Math.round(
          roi.laborSavings,
        ).toLocaleString('en-US')}. Volumes come from our own calls and notes where we stated them, otherwise conservative industry benchmarks; the rate is a conservative regional average.`
      : null;

  function handleShare() {
    const url = buildShareUrl(buildPublicCallBriefSharePayload(result));
    void navigator.clipboard
      ?.writeText(url)
      .then(() => setShareCopied(true))
      .catch(() => setShareCopied(false));
  }

  // Hoist the article to <body> and hide the rest of the app so only the brief
  // prints — no trailing blank pages. Restored on afterprint (+ on focus, for
  // browsers that don't fire it), guarded against double-restore.
  function handleExportPdf() {
    const el = articleRef.current;
    if (!el) {
      window.print();
      return;
    }
    const parent = el.parentNode;
    const next = el.nextSibling;
    let restored = false;
    const restore = () => {
      if (restored) return;
      restored = true;
      document.body.classList.remove('se-print-brief');
      el.removeAttribute('data-brief-print');
      if (parent && el.parentNode !== parent) parent.insertBefore(el, next);
      window.removeEventListener('afterprint', restore);
    };
    el.setAttribute('data-brief-print', '');
    document.body.appendChild(el);
    document.body.classList.add('se-print-brief');
    window.addEventListener('afterprint', restore);
    window.addEventListener('focus', restore, { once: true });
    window.print();
  }

  const tiles = roi
    ? [
        {
          label: 'Hours back / year',
          value: Math.round(roi.annualHoursFreed).toLocaleString('en-US'),
        },
        {
          label: 'Time cost saved',
          value: `$${Math.round(roi.laborSavings).toLocaleString('en-US')}`,
        },
        {
          label: 'Days saved per run',
          value: Math.round(roi.cycleDaysSaved).toLocaleString('en-US'),
        },
      ]
    : [];

  return (
    <article ref={articleRef} className="se-report se-dealbrief">
      {showActions && (
        <div className="se-actions no-print no-share">
          <button type="button" className="se-action se-action--primary" onClick={handleExportPdf}>
            Export PDF
          </button>
          <button type="button" className="se-action" onClick={handleShare}>
            {shareCopied ? 'Link copied' : 'Copy share link'}
          </button>
        </div>
      )}

      <header className="db-masthead">
        <MoxoLogo size={26} />
        <span className="db-masthead__label">Deal Brief</span>
        <span className="db-masthead__co">{company.name}</span>
      </header>

      {/* The 30-second read: one headline, then the numbers. */}
      <section className="db-hero">
        <h1 className="db-hero__headline">{brief.headline}</h1>
        <p className="db-hero__summary">{company.summary}</p>
      </section>

      {tiles.length > 0 && (
        <section className="db-figures" data-testid="call-roi-tiles">
          {tiles.map((t) => (
            <div key={t.label} className="db-figure">
              <div className="db-figure__value">{t.value}</div>
              <div className="db-figure__label">{t.label}</div>
            </div>
          ))}
          {roiBasis && (
            <span
              className="se-tip db-figures__tip no-print"
              tabIndex={0}
              role="note"
              aria-label={`How we estimated this. ${roiBasis}`}
            >
              <span className="se-tip__icon" aria-hidden="true">i</span>
              <span className="se-tip__pop">
                <b className="se-tip__title">How we got this</b>
                {roiBasis}
              </span>
            </span>
          )}
        </section>
      )}

      {/* The picture — the same process today and on Moxo. */}
      <BeforeAfterFlow before={brief.before} after={brief.after} alignment={brief.alignment} />

      {/* The supporting detail, packed but not stuffed. */}
      <div className="db-cols">
        {brief.painPoints.length > 0 && (
          <section className="db-col db-col--cost">
            <h2 className="db-col__title">What it costs us today</h2>
            <ul className="db-col__list">
              {brief.painPoints.map((p, i) => (
                <li key={`pain-${i}`}>{p}</li>
              ))}
            </ul>
          </section>
        )}

        {brief.moxoBenefits.length > 0 && (
          <section className="db-col db-col--gain">
            <h2 className="db-col__title">What changes with Moxo</h2>
            <ul className="db-col__benefits">
              {brief.moxoBenefits.map((b, i) => (
                <li key={`ben-${i}`}>
                  <strong>{b.title}</strong>
                  <span>{b.detail}</span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>

      {(brief.roiNarrative || brief.whyNow.length > 0) && (
        <section className="db-close">
          {brief.roiNarrative && (
            <p className="db-close__prose">
              <span className="db-close__kicker">Where the time comes back</span>
              {brief.roiNarrative}
            </p>
          )}
          {brief.whyNow.length > 0 && (
            <div className="db-why">
              <span className="db-why__kicker">Why now</span>
              <ul className="db-why__items">
                {brief.whyNow.map((w, i) => (
                  <li key={`why-${i}`} className="db-why__item">
                    {w}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      {brief.decisionAsk && (
        <section className="db-ask">
          <span className="db-ask__kicker">What we&rsquo;re asking for</span>
          <p className="db-ask__text">{brief.decisionAsk}</p>
        </section>
      )}

      <footer className="db-foot">
        <p className="db-foot__line">
          {brief.reviewedLine}
          {brief.useCase ? ` Process in focus: ${brief.useCase}.` : ''} A deliberately conservative
          estimate — a figure to confirm in a pilot.
          {result.generatedAt ? ` Prepared ${formatDate(result.generatedAt)}.` : ''}
        </p>
        {roiBasis && <p className="se-roi-basis">{roiBasis}</p>}
      </footer>
    </article>
  );
}
